import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import TitledField from '../../../../Components/Utils/TitledField/TitledField';  
import CreationBar from '../../../../Components/Utils/Actions/CreationBar/CreationBar';  
import ActionBar from '../../../../Components/Utils/Actions/ActionBar/ActionBar';  
import Checkbox from '../../../../Components/Utils/Checkbox/Checkbox';
import layout from '../../../../Modules/layout.module.css';  
import { FieldType, IDynamicField, IDynamicFieldPrototype, SpecialField } from '../../../../Types/Products/IDynamicField';  
import Button from '../../../../Components/Utils/Button/Button';
import styles from './DefineFields.module.css'
import { GetFieldPrototypesByTemplateId, CreateFieldPrototype, DeleteFieldPrototypesByIdList, UpdateFieldPrototype, ReorderFieldInTemplate, GetDevelopTemplateSettings, UpdateDevelopTemplateSettings, type DevelopTemplateSettings } from '../../../../API/TemplateBank/TemplateBank';
import MultiCreationBar, { FieldTypeInfo } from '../../../../Components/Utils/Actions/MultiCreationBar/MultiCreationBar';
import { GetFieldTypeName } from '../../../../Data/FieldTypesData';
import { getClient } from '../../../../Redux/Store/Slices/client';
import { useAppSelector } from '../../../../Redux/Store/Store';
import { useWorkflowContext } from '../../../../Hooks/useWorkflowContext';
import { axiosInstance } from '../../../../Authentication/AxiosInterceptor';
import { motion, AnimatePresence } from 'framer-motion';
import { getProductTypeFromPath } from '../../../../Helpers/Paths';
import { ProductType } from '../../../../Types/ProductType';
import OptionsEditorPopup from '../../../../Components/Utils/OptionsEditorPopup/OptionsEditorPopup';
import { beginPendingSave } from '../../../../Hooks/useUnsavedChangesWarning';

const fieldTypes : FieldTypeInfo[] = [
  { title: 'Text', type: FieldType.Text },
  { title: 'Date', type: FieldType.Date }
];


  
type DefineFieldsProps = object;

const debounceDelay = 500;
  
const DefineFields = ({}: DefineFieldsProps) => {  
  const { templateId, templateTitle } = useParams();  
  const { getWorkflowPath, getBackPath } = useWorkflowContext();
  const updateTimerRef = useRef<NodeJS.Timeout | null>(null);
  const endPendingSaveRef = useRef<(() => void) | null>(null);
  const endPendingDevelopNameSaveRef = useRef<(() => void) | null>(null);

  const client = useAppSelector(getClient);

  const location = useLocation();
  

  /*const temp: IDynamicFieldPrototype[] = [  
    {
      id: "1",
      templateId: undefined,
      title: "What We Do",
      content: "",
      type: FieldType.Text,
      specialField: SpecialField.None,
      archived: false
    },  
    {
      id: "2",
      templateId: undefined,
      title: "What We Do Well",
      content: "",
      type: FieldType.Text,
      specialField: SpecialField.None,
      archived: false
    },  
    {
      id: "3",
      templateId: undefined,
      title: "Areas For Improvement",
      content: "",
      type: FieldType.Text,
      specialField: SpecialField.AreasForImprovement,
      archived: false
    }  
  ];  */
  
  const [selectedItems, setSelectedItems] = useState<string[]>([]);  
  const [fields, setDynamicFields] = useState<IDynamicFieldPrototype[]>([]);  
  const [developSettings, setDevelopSettings] = useState<DevelopTemplateSettings>({
    personShown: false,
    picklistShown: false,
    dropdownShown: false,
    personName: "Person",
    picklistName: "Picklist",
    dropdownName: "Dropdown",
    picklistOptions: [],
    dropdownOptions: [],
  });
  const [editingOptionsType, setEditingOptionsType] = useState<'picklist' | 'dropdown' | null>(null);
  const developNameUpdateTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  useEffect(() => {  

    if(templateId && client) {
      const getFields = async () => {
        const response = await GetFieldPrototypesByTemplateId(templateId, client.id);
        setDynamicFields(response);
      }
      getFields();
    }
    
  }, [templateId]);

  useEffect(() => {
    const loadDevelopSettings = async () => {
      const isDevelop = getProductTypeFromPath(location.pathname) === ProductType.Develop;
      if (!isDevelop || !templateId || !client?.id) return;
      try {
        const settings = await GetDevelopTemplateSettings(templateId, client.id);
        setDevelopSettings({
          personShown: settings.personShown ?? false,
          picklistShown: settings.picklistShown ?? false,
          dropdownShown: settings.dropdownShown ?? false,
          personName: settings.personName ?? "Person",
          picklistName: settings.picklistName ?? "Picklist",
          dropdownName: settings.dropdownName ?? "Dropdown",
          picklistOptions: settings.picklistOptions ?? [],
          dropdownOptions: settings.dropdownOptions ?? [],
        });
      } catch {
        // Template may not support Develop settings (e.g. legacy)
      }
    };
    loadDevelopSettings();
  }, [location.pathname, templateId, client?.id]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (updateTimerRef.current) {
        clearTimeout(updateTimerRef.current);
      }
      if (developNameUpdateTimerRef.current) {
        clearTimeout(developNameUpdateTimerRef.current);
      }
      endPendingSaveRef.current?.();
      endPendingSaveRef.current = null;
      endPendingDevelopNameSaveRef.current?.();
      endPendingDevelopNameSaveRef.current = null;
    };
  }, []);  
  
  const itemSelected = (itemId: string, selected: boolean) => {  
    setSelectedItems((prevState) => {  
      if (selected) {  
        // Add itemId to the list  
        return [...prevState, itemId];  
      } else {  
        // Remove itemId from the list  
        return prevState.filter((id) => id !== itemId);  
      }  
    });  
  };  
  
  const handleTitleChange = async (itemId: string, newTitle: string) => {  
    const field = fields.find((field) => field.id === itemId);

    setDynamicFields((prevFields) =>  
      prevFields.map((field) =>  
        field.id === itemId ? { ...field, title: newTitle } : field  
      )  
    );

    if (!field || !client) return;

    if (updateTimerRef.current) {
      clearTimeout(updateTimerRef.current);
    } else if (!endPendingSaveRef.current) {
      endPendingSaveRef.current = beginPendingSave();
    }

    updateTimerRef.current = setTimeout(async () => {
      try {
        const updatedField = { ...field, title: newTitle };
        await UpdateFieldPrototype(updatedField, client.id);
      } catch (error) {
        console.error('Error updating field title:', error);
      } finally {
        updateTimerRef.current = null;
        endPendingSaveRef.current?.();
        endPendingSaveRef.current = null;
      }
    }, debounceDelay);
  };  
  
  const handleTextChange = async (itemId: string, text: string) => {  
    const field = fields.find((field) => field.id === itemId);

    setDynamicFields((prevFields) =>  
      prevFields.map((field) =>  
        field.id === itemId ? { ...field, placeholder: text } : field  
      )  
    );

    if (!field || !client) return;

    if (updateTimerRef.current) {
      clearTimeout(updateTimerRef.current);
    } else if (!endPendingSaveRef.current) {
      endPendingSaveRef.current = beginPendingSave();
    }

    updateTimerRef.current = setTimeout(async () => {
      try {
        const updatedField = { ...field, placeholder: text };
        await UpdateFieldPrototype(updatedField, client.id);
      } catch (error) {
        console.error('Error updating field placeholder:', error);
      } finally {
        updateTimerRef.current = null;
        endPendingSaveRef.current?.();
        endPendingSaveRef.current = null;
      }
    }, debounceDelay);
  };

  const handleLockToggle = async (itemId: string) => {
    const field = fields.find((f) => f.id === itemId);
    if (!field || !client) return;

    const updatedField = { ...field, locked: !field.locked };
    
    try {
      await UpdateFieldPrototype(updatedField, client.id);
      setDynamicFields((prev) =>
        prev.map((f) =>
          f.id === itemId ? { ...f, locked: !f.locked } : f
        )
      );
    } catch (error) {
      console.error('Error toggling field lock:', error);
    }
  };  
  
  const getNextAvailableSpecialField = () => {  

    const type = getProductTypeFromPath(location.pathname);
  
    const usedSpecialFields = fields.map(field => field.specialField);  
    var specialFieldValues = Object.values(SpecialField).filter(value => typeof value === 'number') as number[];  

    if(type === ProductType.Develop){
      // For Develop, exclude Text1, Text2, and Text3 from available special fields
      specialFieldValues = specialFieldValues.filter(value => value !== SpecialField.Text1 && value !== SpecialField.Text2 && value !== SpecialField.Text3);
    }

    for (const value of specialFieldValues) {  
        if (
            !usedSpecialFields.includes(value) &&
            value !== SpecialField.None &&
            value !== SpecialField.Title &&
            value !== SpecialField.FullQuestion &&
            value !== SpecialField.AreasForImprovement
        ) {  
            return value;  
        }  
    }  
    
    return SpecialField.None; // Default to None if all special fields are used  
};  

const createItem = async (title: string) => {  
    if (templateId && client) {  
        const specialField = getNextAvailableSpecialField();

        if (specialField === SpecialField.None) {  
            alert('All fields are used');  
            return;  
        }

        const newField = await CreateFieldPrototype(title, FieldType.Text, templateId, specialField, client.id);  
        setDynamicFields([...fields, newField]);  
    }  
};  

  const deleteItems = async () => {  

    await DeleteFieldPrototypesByIdList(selectedItems);

    setDynamicFields((prevFields) =>  
      prevFields.filter((field) => !selectedItems.includes(field.id)) 
    );  

    setSelectedItems([]); // Clear the selected items after deletion  
  };

  const handleMoveField = async (fieldId: string, direction: 'up' | 'down') => {
    if (!templateId || !client) return;

    try {
      const updatedFields = await ReorderFieldInTemplate(templateId, fieldId, direction, client.id);
      setDynamicFields(updatedFields);
    } catch (error) {
      console.error('Error reordering field:', error);
    }
  };  

  const updateDevelopTemplateSettings = async (next: DevelopTemplateSettings) => {
    setDevelopSettings(next);
    if (templateId && client?.id) {
      try {
        await UpdateDevelopTemplateSettings(templateId, client.id, next);
      } catch (e) {
        console.error('Failed to update template settings', e);
        setDevelopSettings(developSettings);
      }
    }
  };

  const handleDevelopToggleChange = async (field: 'personShown' | 'picklistShown' | 'dropdownShown', value: boolean) => {
    const next: DevelopTemplateSettings = { ...developSettings, [field]: value };
    await updateDevelopTemplateSettings(next);
  };

  const handleSaveDevelopOptions = async (field: 'picklistOptions' | 'dropdownOptions', options: string[]) => {
    const next: DevelopTemplateSettings = { ...developSettings, [field]: options };
    await updateDevelopTemplateSettings(next);
  };

  const handleDevelopNameChange = (field: 'personName' | 'picklistName' | 'dropdownName', value: string, flushNow: boolean) => {
    if (developNameUpdateTimerRef.current) {
      clearTimeout(developNameUpdateTimerRef.current);
    }

    const next: DevelopTemplateSettings = { ...developSettings, [field]: value };
    setDevelopSettings(next);

    if (flushNow || !templateId || !client?.id) {
      endPendingDevelopNameSaveRef.current?.();
      endPendingDevelopNameSaveRef.current = null;
      updateDevelopTemplateSettings(next);
      return;
    }

    if (!endPendingDevelopNameSaveRef.current) {
      endPendingDevelopNameSaveRef.current = beginPendingSave();
    }

    developNameUpdateTimerRef.current = setTimeout(async () => {
      try {
        await updateDevelopTemplateSettings(next);
      } finally {
        developNameUpdateTimerRef.current = null;
        endPendingDevelopNameSaveRef.current?.();
        endPendingDevelopNameSaveRef.current = null;
      }
    }, debounceDelay);
  };

  
  return (  
    <div className={layout.container}>  
      <div className={`${layout.column1} ${styles.pageShell}`}>  
        <div className={styles.pageHeader}>
          <div className={styles.pageHeaderEyebrow}>Template Fields</div>
          <h1 className={styles.pageTitle}>{templateTitle}</h1>    
          <p className={styles.pageSubtitle}>Create the fields for this diagnostic</p>     
        </div>    

        <div className={styles.actionBarSection}>
          <ActionBar enabledFeatures={['delete']} selectedCount={selectedItems.length} onDeleteHandler={deleteItems}/>  
        </div>

        <div className={styles.fieldsSection}>
          <div className={styles.sectionGroupHeader}>
            <div>
              <p className={styles.sectionGroupTitle}>Fields</p>
              <p className={styles.sectionGroupHint}>Define the editable fields used throughout this template.</p>
            </div>
            <span className={styles.sectionCountBadge}>{fields.length} fields</span>
          </div>
        <AnimatePresence mode="popLayout">
        {fields.map(({ id, title, content, mandatory, type, specialField, locked, placeholder }, index) => {  
          return (  
            <motion.div 
              key={id} 
              layout
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ 
                duration: 0.2,
                layout: { duration: 0.3, ease: "easeInOut" }
              }}
              className={styles.fieldContainer}
            >
              <div className={styles.fieldField}>
                <TitledField  
                  title={title}  
                  text={placeholder || ''}  
                  onSelectedHandler={(selected: boolean) => itemSelected(id, selected)}  
                  titleChanged={(newTitle: string) => handleTitleChange(id, newTitle)}  
                  valueChanged={(text: string) => handleTextChange(id, text)}  
                  locked={locked}
                  onLockToggle={specialField === SpecialField.AreasForImprovement ? undefined : () => handleLockToggle(id)}
                  specialField={specialField}
                />
              </div>
              <div className={styles.reorderButtons}>
                <button 
                  className={styles.reorderButton}
                  onClick={() => handleMoveField(id, 'up')}
                  disabled={index === 0}
                  title="Move up"
                >
                  ▲
                </button>
                <button 
                  className={styles.reorderButton}
                  onClick={() => handleMoveField(id, 'down')}
                  disabled={index === fields.length - 1}
                  title="Move down"
                >
                  ▼
                </button>
              </div>
            </motion.div>
          );  
        })}
        </AnimatePresence>
        </div>

        <div className={styles.createSectionCard}>
          <div className={styles.settingsCardHeader}>
            <p className={styles.settingsCardTitle}>Add a New Field</p>
            <p className={styles.settingsCardHint}>Create another field for this diagnostic template.</p>
          </div>
          <CreationBar onCreateHandler={createItem} placeholder={'Enter title of new field...'} noTextMessage={'*'}/>
        </div>

        {getProductTypeFromPath(location.pathname) === ProductType.Develop && (
          <div className={styles.optionalFieldsSection}>
            <div className={styles.optionalFieldsHeader}>
              <p className={styles.optionalFieldsTitle}>Optional Fields</p>
              <p className={styles.optionalFieldsHint}>Add additional fields inside each criteria when using this template.</p>
            </div>
            <div className={styles.optionalFieldsGrid}>
              <label className={`${styles.checkboxLabel} ${developSettings.personShown ? styles.checkboxLabelActive : ''}`}>
                <Checkbox
                  clickHandler={(selected: boolean) => handleDevelopToggleChange('personShown', selected)}
                  value={developSettings.personShown}
                />
                <div className={styles.fieldToggleCopy}>
                  <span className={styles.fieldToggleTitle}>{developSettings.personName ?? "Person"}</span>
                  <span className={styles.fieldToggleMeta}>Assign a participant to a criteria</span>
                </div>
              </label>
              <label className={`${styles.checkboxLabel} ${developSettings.picklistShown ? styles.checkboxLabelActive : ''}`}>
                <Checkbox
                  clickHandler={(selected: boolean) => handleDevelopToggleChange('picklistShown', selected)}
                  value={developSettings.picklistShown}
                />
                <div className={styles.fieldToggleCopy}>
                  <span className={styles.fieldToggleTitle}>{developSettings.picklistName ?? "Picklist"}</span>
                  <span className={styles.fieldToggleMeta}>Add a customisable picklist field to each criteria</span>
                </div>
              </label>
              <label className={`${styles.checkboxLabel} ${developSettings.dropdownShown ? styles.checkboxLabelActive : ''}`}>
                <Checkbox
                  clickHandler={(selected: boolean) => handleDevelopToggleChange('dropdownShown', selected)}
                  value={developSettings.dropdownShown}
                />
                <div className={styles.fieldToggleCopy}>
                  <span className={styles.fieldToggleTitle}>{developSettings.dropdownName ?? "Dropdown"}</span>
                  <span className={styles.fieldToggleMeta}>Add a customisable single-select dropdown field to each criteria</span>
                </div>
              </label>
            </div>

            {(developSettings.personShown || developSettings.picklistShown || developSettings.dropdownShown) && (
              <div className={styles.fieldTitlesGrid}>
                {developSettings.personShown && (
                  <div className={styles.fieldTitleEditor}>
                    <p className={styles.optionsLabel}>Person field title</p>
                    <input
                      className={styles.criteriaTitleInput}
                      value={developSettings.personName}
                      onChange={(e) => handleDevelopNameChange('personName', e.target.value, false)}
                      onBlur={(e) => handleDevelopNameChange('personName', e.target.value, true)}
                      placeholder="Person"
                    />
                  </div>
                )}

                {developSettings.picklistShown && (
                  <div className={styles.fieldTitleEditor}>
                    <p className={styles.optionsLabel}>Picklist field title</p>
                    <input
                      className={styles.criteriaTitleInput}
                      value={developSettings.picklistName}
                      onChange={(e) => handleDevelopNameChange('picklistName', e.target.value, false)}
                      onBlur={(e) => handleDevelopNameChange('picklistName', e.target.value, true)}
                      placeholder="Picklist"
                    />
                  </div>
                )}

                {developSettings.dropdownShown && (
                  <div className={styles.fieldTitleEditor}>
                    <p className={styles.optionsLabel}>Dropdown field title</p>
                    <input
                      className={styles.criteriaTitleInput}
                      value={developSettings.dropdownName}
                      onChange={(e) => handleDevelopNameChange('dropdownName', e.target.value, false)}
                      onBlur={(e) => handleDevelopNameChange('dropdownName', e.target.value, true)}
                      placeholder="Dropdown"
                    />
                  </div>
                )}
              </div>
            )}

            {(developSettings.picklistShown || developSettings.dropdownShown) && (
              <div className={styles.optionsButtons}>
                {developSettings.picklistShown && (
                  <div className={styles.optionsAction}>
                    <div className={styles.optionsActionHeader}>
                      <div>
                        <p className={styles.optionsLabel}>Picklist Options</p>
                        <p className={styles.optionsHint}>These appear as multi-select chips on each Develop criteria.</p>
                      </div>
                      <span className={styles.optionsCountBadge}>{developSettings.picklistOptions?.length ?? 0} defined</span>
                    </div>
                    <button
                      type="button"
                      className={styles.editOptionsTrigger}
                      onClick={() => setEditingOptionsType('picklist')}
                    >
                      {developSettings.picklistOptions?.length ? 'Edit Picklist Options' : 'Define Picklist Options'}
                    </button>
                  </div>
                )}
                {developSettings.dropdownShown && (
                  <div className={styles.optionsAction}>
                    <div className={styles.optionsActionHeader}>
                      <div>
                        <p className={styles.optionsLabel}>Dropdown Options</p>
                        <p className={styles.optionsHint}>These appear as a single-select dropdown on each Develop criteria.</p>
                      </div>
                      <span className={styles.optionsCountBadge}>{developSettings.dropdownOptions?.length ?? 0} defined</span>
                    </div>
                    <button
                      type="button"
                      className={styles.editOptionsTrigger}
                      onClick={() => setEditingOptionsType('dropdown')}
                    >
                      {developSettings.dropdownOptions?.length ? 'Edit Dropdown Options' : 'Define Dropdown Options'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        <OptionsEditorPopup
          isVisible={editingOptionsType === 'picklist'}
          title="picklist"
          options={developSettings.picklistOptions ?? []}
          onClose={() => setEditingOptionsType(null)}
          onSave={(options) => handleSaveDevelopOptions('picklistOptions', options)}
        />
        <OptionsEditorPopup
          isVisible={editingOptionsType === 'dropdown'}
          title="dropdown"
          options={developSettings.dropdownOptions ?? []}
          onClose={() => setEditingOptionsType(null)}
          onSave={(options) => handleSaveDevelopOptions('dropdownOptions', options)}
        />

        <div className={`${layout.column1} ${layout.container}`}>       
          <div className={styles.footerLabel}>Next please define the structure of your diagnostic</div>          
          <div className={`${layout.rowSpan} ${styles.footerBar}`}>   
            <Link to={getBackPath()}>
              <Button>Back</Button>
            </Link>
            <Link to={getWorkflowPath(templateId || '', templateTitle || '', 'define-structure')}>
              <Button>Next</Button>
            </Link>            
          </div>            
        </div>          
      </div>  
    </div>  
  );  
};  
  
export default DefineFields;  